import React, { useState } from 'react';
import { Send, MapPin, Phone, Mail, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

export const ContactForm: React.FC = () => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const form = new FormData(e.currentTarget);
      form.append('access_key', import.meta.env.VITE_WEB3FORMS_ACCESS_KEY);
      form.append('subject', `New Contact Form Submission from ${formData.name}`);
      form.append('from_name', 'VMIL Website');

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: form,
      });

      const result = await response.json();

      if (result.success) {
        setIsSuccess(true);
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
        setTimeout(() => setIsSuccess(false), 5000);
      } else {
        console.error('Form submission failed:', result);
        alert('Failed to send message. Please try again or contact us directly at contactus@vmil.in');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Failed to send message. Please try again or contact us directly at contactus@vmil.in');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Address',
      lines: ['Thuvakkudy, Trichy – 620 015', 'Tamil Nadu, South India'],
      link: 'https://maps.google.com/maps?q=Developed%20Plot%20Estate%2C%20E53%2C54%2C%20Thuvakudi%2C%20Tamil%20Nadu%20620015%2C%20India',
    },
    {
      icon: Phone,
      title: 'Phone',
      lines: ['+91 431 2500 547'],
      link: 'tel:+914312500547',
    },
    {
      icon: Mail,
      title: 'Email',
      lines: ['contactus@vmil.in'],
      link: 'mailto:contactus@vmil.in',
    },
    {
      icon: Clock,
      title: 'Working Hours',
      lines: ['Mon - Sat: 9:00 AM - 6:00 PM'],
    },
  ];

  return (
    <section className={`w-full py-16 md:py-24 relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-vmil-navy' : 'bg-vmil-light'
    }`}>
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className={`inline-flex items-center px-4 py-2 rounded-full border mb-6 ${
            isDark 
              ? 'bg-vmil-blue/10 border-vmil-blue/20' 
              : 'bg-vmil-blue/10 border-vmil-blue/20'
          }`}>
            <span className={`text-sm font-semibold uppercase tracking-wide ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`}>
              Get In Touch
            </span>
          </div>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-extrabold mb-6 leading-tight ${
            isDark ? 'text-white' : 'text-vmil-blue'
          }`}>
            Contact Us
          </h2>
          <p className={`text-lg max-w-2xl mx-auto ${
            isDark ? 'text-white/70' : 'text-vmil-steel'
          }`}>
            Have a project in mind? Get in touch with us to discuss how we can help 
            meet your industrial equipment needs.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <div className="grid grid-cols-2 gap-3 sm:gap-6 mb-6 sm:mb-8">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                const Content = (
                  <div className={`p-3 sm:p-6 rounded-xl sm:rounded-2xl h-full transition-all hover:-translate-y-1 ${
                    isDark 
                      ? 'bg-white/5 border border-white/10 hover:border-vmil-blue/30' 
                      : 'bg-white border border-gray-200 shadow-sm hover:shadow-lg'
                  }`}>
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4 ${
                      isDark ? 'bg-vmil-blue/20' : 'bg-vmil-blue/10'
                    }`}>
                      <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`} />
                    </div>
                    <h3 className={`font-bold text-sm sm:text-base mb-1 sm:mb-2 ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
                      {info.title}
                    </h3>
                    {info.lines.map((line, i) => (
                      <p key={i} className={`text-xs sm:text-sm ${isDark ? 'text-white/70' : 'text-vmil-steel'}`}>
                        {line}
                      </p>
                    ))}
                  </div>
                );

                return info.link ? (
                  <a 
                    key={index} 
                    href={info.link} 
                    target={info.link.startsWith('http') ? '_blank' : undefined}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="block"
                  >
                    {Content}
                  </a>
                ) : (
                  <div key={index}>{Content}</div>
                );
              })}
            </div>

            {/* Map */}
            <div className={`rounded-xl sm:rounded-2xl overflow-hidden h-48 sm:h-64 ${
              isDark ? 'border border-white/10' : 'border border-gray-200'
            }`}>
              <iframe 
                src="https://maps.google.com/maps?q=Developed%20Plot%20Estate%2C%20E53%2C54%2C%20Thuvakudi%2C%20Tamil%20Nadu%20620015%2C%20India&t=m&z=14&output=embed"
                title="VMIL Location"
                className="w-full h-full"
                loading="lazy"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div className={`p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl ${
            isDark 
              ? 'bg-white/5 border border-white/10' 
              : 'bg-white border border-gray-200 shadow-lg'
          }`}>
            <h3 className={`text-xl sm:text-2xl font-bold mb-4 sm:mb-6 ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
              Send us a Message
            </h3>

            {isSuccess ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h4 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
                  Message Sent!
                </h4>
                <p className={isDark ? 'text-white/70' : 'text-vmil-steel'}>
                  We'll get back to you as soon as possible.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDark ? 'text-white/80' : 'text-vmil-steel'
                    }`}>
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-vmil-blue ${
                        isDark 
                          ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40' 
                          : 'bg-white border-gray-300 text-vmil-blue placeholder:text-gray-400'
                      }`}
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDark ? 'text-white/80' : 'text-vmil-steel'
                    }`}>
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-vmil-blue ${
                        isDark 
                          ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40' 
                          : 'bg-white border-gray-300 text-vmil-blue placeholder:text-gray-400'
                      }`}
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDark ? 'text-white/80' : 'text-vmil-steel'
                    }`}>
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-vmil-blue ${
                        isDark 
                          ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40' 
                          : 'bg-white border-gray-300 text-vmil-blue placeholder:text-gray-400'
                      }`}
                      placeholder="Your phone number"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDark ? 'text-white/80' : 'text-vmil-steel'
                    }`}>
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-vmil-blue ${
                        isDark 
                          ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40' 
                          : 'bg-white border-gray-300 text-vmil-blue placeholder:text-gray-400'
                      }`}
                      placeholder="Company name"
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? 'text-white/80' : 'text-vmil-steel'
                  }`}>
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className={`w-full px-4 py-3 rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-vmil-blue resize-none ${
                      isDark 
                        ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40' 
                        : 'bg-white border-gray-300 text-vmil-blue placeholder:text-gray-400'
                    }`}
                    placeholder="Tell us about your project..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full inline-flex items-center justify-center gap-2 h-14 rounded-xl bg-vmil-blue text-white font-bold hover:bg-vmil-blue/90 transition-all disabled:opacity-50 shadow-lg shadow-vmil-blue/25"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

